import * as Sentry from "@sentry/react";

export type CaptchaFailureStage =
  | "configuration"
  | "initialization_request"
  | "script_load"
  | "sdk_initialization"
  | "widget_render"
  | "widget_error"
  | "challenge_timeout"
  | "interaction_timeout"
  | "token_expired"
  | "missing_proof"
  | "validation_rejected"
  | "validation_request"
  | "retry_exhausted";

const getErrorStatus = (status: unknown): number | string | undefined => {
  if (typeof status === "number" && status >= 100 && status <= 599)
    return status;
  if (
    typeof status === "string" &&
    ["FETCH_ERROR", "TIMEOUT_ERROR", "PARSING_ERROR"].includes(status)
  )
    return status;
};

const getFailureContext = (errorCode?: string, status?: unknown) => ({
  error_code: errorCode && /^\d{1,6}$/.test(errorCode) ? errorCode : undefined,
  status: getErrorStatus(status),
  hostname: window.location.hostname,
  pathname: window.location.pathname,
  app_version: /^\d+(\.\d+){0,3}$/.test(window.cornixAppVersion ?? "")
    ? window.cornixAppVersion
    : undefined,
  is_webview: !!window.ReactNativeWebView || !!window.isReactNativeWebView,
  online: navigator.onLine,
});

export const reportCaptchaFailure = (
  provider: "turnstile" | "geetest",
  stage: CaptchaFailureStage,
  errorCode?: string,
  status?: unknown
) => {
  Sentry.captureMessage("CAPTCHA verification failed", {
    level: "warning",
    tags: { captcha_provider: provider, captcha_stage: stage },
    contexts: { captcha: getFailureContext(errorCode, status) },
  });
};

const scripts = new Map<string, Promise<void>>();

export const loadCaptchaScript = (source: string): Promise<void> => {
  const existing = scripts.get(source);
  if (existing) return existing;
  const script = document.createElement("script");
  script.src = source;
  script.async = true;
  const promise = new Promise<void>((resolve, reject) => {
    const fail = () => {
      window.clearTimeout(timeout);
      scripts.delete(source);
      script.remove();
      reject(new Error("Unable to load captcha"));
    };
    const timeout = window.setTimeout(fail, 20000);
    script.onload = () => {
      window.clearTimeout(timeout);
      resolve();
    };
    script.onerror = fail;
    document.head.appendChild(script);
  });
  scripts.set(source, promise);
  return promise;
};
